import type { ZudokuConfig } from "zudoku";
import theme from "./config/theme";
import navigation from "./config/navigation";
import apis from "./config/api";
import plugins from "./config/plugins";
import SiteHeader from "./components/SiteHeader";
import SiteFooter from "./components/SiteFooter";
import PostByline from "./components/PostByline";
import SeoHead from "./components/SeoHead";
import { SITE, KEYCLOAK_ISSUER, TITLE_TEMPLATE } from "./components/brand";

const authentication = {
    type: "openid",
    clientId: "nervai_frontend",
    issuer: KEYCLOAK_ISSUER,
    scopes: ["openid", "profile", "email"],
    // Ensure proper callback configuration
    redirectToAfterSignIn: "/api",
    redirectToAfterSignUp: "/api",
  } as const;

const config = {

  authentication: authentication,
  protectedRoutes: [],

  basePath: "/pub",
  site: {
    title: "HRMLESS Docs",
    // The current teal brand mark — the SAME file components/brand.ts exports as
    // BRAND_IMAGE, so the favicon, the og:image fallback and Organization.logo
    // all resolve to one asset and cannot drift onto different marks.
    //
    // This previously named "/hrmless-logo.svg", which HAS NEVER EXISTED in
    // public/. It failed silently in the worst way: zudoku emits a
    // <link rel="preload" as="image"> for it, so all 255 built pages preloaded a
    // 404 on every view, and nothing looked broken because the element that
    // would have shown it is hidden (see below).
    //
    // NOTE: zudoku's own header is display:none — config/theme.tsx hides it and
    // the custom SiteHeader slot replaces it — so this image is NEVER RENDERED.
    // It is set only so the preload zudoku emits regardless points at a real
    // file. The visible header and footer mark is the inline SVG in
    // components/Logo.tsx, which is unaffected by anything here.
    //
    // Paths are app-root-relative: zudoku prepends basePath itself (verified —
    // the config's "/favicon.png" is emitted as "/pub/favicon.png"), so a
    // leading "/pub" would resolve to /pub/pub/…
    logo: {
      src: {
        light: "/imgs/hrmless-website-logo.png",
        dark: "/imgs/hrmless-website-logo.png"
      },
      alt: "HRMLESS",
      // The mark is square (500x500). The old 150px was sized for a wide
      // wordmark lockup and would have stretched this to a 150px block.
      width: "32px",
      // Follows SITE so the logo returns the visitor to the marketing site of the
      // SAME environment rather than jumping from .dev to prod .com.
      href: SITE
    },
    // Zudoku only mounts its <footer> — and with it the "footer-after" slot that
    // carries SiteFooter — when site.footer is set. Without this, docs and
    // blog-post pages rendered with no footer at all; only BlogHome had one,
    // because that page renders SiteFooter itself. theme.customCss blanks the
    // default footer's own contents, so this exists purely to mount the slot.
    footer: { copyright: "" },
  },

  slots: {
    "layout-before-head": SiteHeader,
    // The author, above the article body. Renders on blog posts only and no-ops
    // elsewhere; like the SeoHead note below, layout slots never reach the blog
    // index (custom-page, layout:"none") — its per-card byline is in BlogHome.
    "content-before": PostByline,
    // Renders nothing visible — it exists to push the og:*, twitter:* and
    // Article JSON-LD tags Zudoku's `metadata` block has no fields for. It
    // deliberately leaves title/description/canonical to Zudoku; see the file.
    // NOTE: Layout slots do not reach the blog index, which is a custom-page
    // with layout:"none". BlogHome.tsx carries its own <Head>.
    "layout-after-head": SeoHead,
    "footer-after": SiteFooter,
  },

  //redirects: [{ from: "", to: "/overview" }],
  apis: apis,
  navigation: navigation,
  plugins: plugins,
  search: { type: "pagefind" },

  docs: {
    files: "pages/**/*.{md,mdx}", // Your markdown files
    publishMarkdown: true, // Generate .md files
    llms: {
      llmsTxt: true, // Generate llms.txt
      llmsTxtFull: true, // Generate llms-full.txt
      includeProtected: false, // Exclude protected routes
    },
  },


  // Zudoku prepends this origin to location.pathname for the <link rel=canonical>
  // it emits on every page (node_modules/zudoku/src/lib/components/Meta.tsx).
  // Without it Zudoku emits no canonical at all, which is what left all 227 blog
  // posts uncanonicalised. It follows SITE so a .dev build cannot advertise .com
  // canonicals — the same rule the sitemap origin already obeys.
  canonicalUrlOrigin: SITE,

  metadata: {
    // TEMPLATE, not a literal. Zudoku passes this to unhead as `titleTemplate`,
    // and unhead substitutes %s with the page's own title (from frontmatter, or
    // the page's h1). This used to be the bare site name with no %s, so every
    // page — all 227 blog posts included — rendered <title>HRMLESS - Less HRM.
    // More Productivity</title> and competed with itself in search results.
    // The per-page titles were always there; the template was throwing them away.
    // Imported rather than written here: components/SeoHead.tsx measures the
    // suffix off this same string to work out how much room a post title has
    // (see seoTitle in brand.ts). Two copies would let the budget drift.
    "title": TITLE_TEMPLATE,
    // Used INSTEAD of the template on a page with no title of its own, where
    // "%s | HRMLESS" would render a leading separator. Every route in the
    // current build resolves a real title through the template — including /api
    // and the blog index — so this is a safety net for future pages, not
    // something presently doing work.
    "defaultTitle": "HRMLESS - Less HRM. More Productivity",
    "description": "HRMLESS Docs",
    "logo": "/imgs/hrmless-website-logo.png",
    // Was "/favicon.png" — emitted as /pub/favicon.png, which does not exist in
    // public/, so every page requested a 404 and browsers fell back to the
    // default globe. Same square 500x500 mark as the logo above; app-root
    // relative for the same basePath reason.
    //
    // Deliberately NOT public/logo-micro-120x120.png despite that file being
    // favicon-sized: it is the RETIRED PURPLE MARK, not current branding.
    "favicon": "/imgs/hrmless-website-logo.png",
    "applicationName": "HRMLESS Docs",
    "referrer": "no-referrer",
    "keywords": [
      "HRMLESS", 
      "documentation", 
      "metadata", 
      "SEO", 
      "AI", 
      "ATS", 
      "AI-interviews", 
      "HRM", 
      "recruitment", 
      "HR", 
      "HR technology", 
      "HR software", 
      "HR management", 
      "HR solutions", 
      "ai screening interview", 
      "ai recruitment platform",
      "hiring managers ai",
      "ai recruiting platforms",
      "ai interview example",
      "candidate ranking system",
      "interview", 
      "Applicant Tracking",
      "Applicant Tracking System",
      "Amazon",
      "DSP",
      "Amazon DSP",
      "Delivery Service Partner",
      "last mile delivery"
    ],
    "authors": ["HRMLESS Team"],
    "creator": "contactus@hrmless.com",
    "publisher": "HRMLESS, LLC"
  },

  theme: theme,
} satisfies ZudokuConfig;

export default config;
